import {
  ErrorMessage,
  Mtds,
  Sample,
  Task,
  TopBar,
  AppLayout,
  XIcon,
  getFirstRequisition,
  getSubstanceText,
  templates,
  useGetBatchDataQuery,
  useGetSampleByIdQuery,
  useGetTaskByIdQuery,
  useGetTasksQuery,
  useGoBack,
  widgets,
  useGetPlanSummariesQuery,
  ScreenHeader
} from 'prove-common'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  getMoveSampleSchema,
  MoveSampleFormData
} from '../schemas/move-sample-schema'
import { moveSampleUiSchema } from '../schemas/move-sample-ui-schema'
import TaskList from '../components/task-list/TaskList'
import { filterTaskList, getTaskList } from '../utils/taskHelper'
import { SampleEditForm } from '../components/sample-edit-form/SampleEditForm'
import { AppLogo } from '../components/app-logo/AppLogo'
import { Form, validator } from 'prove-common/rjsf'

export const SampleMoveScreen = () => {
  const { sampleId } = useParams()
  const navigate = useNavigate()
  const goBack = useGoBack(`/samples/${sampleId}`)
  const [searchParams, setSearchParams] = useSearchParams()
  const newTaskId = searchParams.get('newTaskId')
  const [formData, setFormData] = useState<MoveSampleFormData>()
  const {
    data: plans,
    isPending: isPendingPlans,
    isError: isErrorPlans,
    error: plansError
  } = useGetPlanSummariesQuery()
  const {
    data: sample,
    isPending: isPendingSample,
    isError: isErrorSample,
    error: sampleError
  } = useGetSampleByIdQuery(sampleId)
  const {
    data: currentTask,
    isPending: isPendingTask,
    isError: isErrorTask,
    error: taskError
  } = useGetTaskByIdQuery(sample?.taskId)
  const {
    data: tasks,
    isPending: isPendingTasks,
    isError: isErrorTasks,
    error: tasksError
  } = useGetTasksQuery({
    ownerId: currentTask?.ownerId == null ? undefined : [currentTask.ownerId]
  })
  const {
    data: batchData,
    isPending: isPendingBatchData,
    isError: isErrorBatchData,
    isSuccess: isSuccessBatchData,
    error: batchDataError
  } = useGetBatchDataQuery()

  const taskList = getTaskList(tasks ?? [], plans)
  const filteredTaskList = filterTaskList(taskList, {
    labId: currentTask?.lab?.id.toString(),
    productIds: formData?.productId == null ? [] : [formData.productId],
    substanceIds: formData?.substanceIds ?? [],
    matrixIds: formData?.matrixId == null ? [] : [formData.matrixId],
    sampntTypeIds: [],
    planIds: [],
    status: []
  })
  const requisition = getFirstRequisition(sample)
  const newTask: Task | undefined = tasks?.find(
    (task) => task.id === Number(newTaskId)
  )
  const hasTaskContentChanged =
    sample != null &&
    newTask != null &&
    ((sample.sampleType?.product ?? undefined) !==
      (newTask.analysisType?.sampleType?.product ?? undefined) ||
      (sample.sampleType?.matrix ?? undefined) !==
        (newTask.analysisType?.sampleType?.matrix ?? undefined) ||
      (requisition?.analysisType?.id ?? undefined) !==
        (newTask.analysisType?.id ?? undefined))
  const breadcrumbItems = [
    {
      label: 'Prøver',
      to: '/samples'
    },
    {
      label: '#' + sampleId,
      to: `/samples/${sampleId}`
    },
    {
      label: 'Flytt'
    }
  ]
  const isLoading =
    isPendingSample || isPendingTask || isPendingTasks || isPendingPlans
  const isError = isErrorSample || isErrorTask || isErrorTasks || isErrorPlans
  const error = plansError ?? sampleError ?? taskError ?? tasksError
  return (
    <AppLayout
      isLoading={isLoading}
      isError={isError}
      error={error}
      topBar={<TopBar logo={<AppLogo />} breadcrumbItems={breadcrumbItems} />}
    >
      {newTaskId == null && (
        <>
          <ScreenHeader
            title='Flytt til en annen oppgave'
            description={
              <p>Velg hvilke produkter prøven egentlig er tatt av.</p>
            }
          />

          {isPendingBatchData && !isErrorBatchData && <Mtds.Skeleton />}
          {isErrorBatchData && <ErrorMessage error={batchDataError} />}
          {isSuccessBatchData && (
            <Form
              noHtml5Validate
              schema={getMoveSampleSchema(batchData)}
              uiSchema={moveSampleUiSchema}
              widgets={widgets}
              templates={templates}
              showErrorList={false}
              validator={validator}
              formData={formData}
              onChange={(data) => setFormData(data.formData)}
              experimental_defaultFormStateBehavior={{
                constAsDefaults: 'skipOneOf'
              }}
            >
              <></>
            </Form>
          )}
          <Mtds.Grid className={'mt-4'}>
            <Mtds.Heading as='h4'>Nåværende oppgave</Mtds.Heading>
            <Mtds.Table data-fixed>
              <tbody>
                <tr>
                  <th>Produkt</th>
                  <td>{sample?.sampleType.product ?? '-'}</td>
                </tr>
                <tr>
                  <th>Prøve av</th>
                  <td>{sample?.sampleType.matrix ?? '-'}</td>
                </tr>
                <tr>
                  <th>Analyser for</th>
                  <td>{getSubstanceText(requisition?.analysisType)}</td>
                </tr>
              </tbody>
            </Mtds.Table>
          </Mtds.Grid>
          <Mtds.Grid className={'mt-4'}>
            <Mtds.Heading as='h4'>Velg ny oppgave</Mtds.Heading>
            {isPendingTasks ? (
              <Mtds.Spinner>Leter etter oppgaver...</Mtds.Spinner>
            ) : (
              <TaskList
                taskItems={filteredTaskList}
                onClick={(taskId) => {
                  setSearchParams({ newTaskId: taskId.toString() })
                }}
                isFiltering={false}
              />
            )}
          </Mtds.Grid>
          <Mtds.Flex>
            <Mtds.Button data-variant='secondary' onClick={goBack}>
              <XIcon />
              Avbryt
            </Mtds.Button>
          </Mtds.Flex>
        </>
      )}

      {newTaskId != null && sample != null && (
        <>
          <ScreenHeader
            title='Fyll ut skjema'
            description={
              <p>Kontroller at følgende informasjon fortsatt stemmer.</p>
            }
          />
          <SampleEditForm
            sample={sample}
            newTask={newTask}
            showReceiptInfoOnSave={hasTaskContentChanged}
            onSuccess={(oldSample: Sample) =>
              navigate(`/samples/${sampleId}/edit-completed`, {
                state: { oldSample }
              })
            }
          />
        </>
      )}
    </AppLayout>
  )
}
